import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CandidateListing } from "../../../api/content/curationApi";
import { keepNewTerms, markUnitsOk } from "../../../api/content/reviewCollaborationApi";
import type { Review } from "../../../api/content/reviewDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { iriLocalName } from "../../../shared/references/shortReferences";
import type { OutlineRow } from "../outline/reviewOutline";
import { bulkKeepableCandidates, bulkMarkableRows } from "./bulkSelection";
import OptionLabel from "../../../ui/forms/OptionLabel";

/**
 * Bulk decisions, only where safe (curation-ui.md CU-9): mark OK the units I curate that
 * have nothing open about them, and keep the new terms whose possible duplicates scored
 * low. Each shows the exact list, which can be narrowed, before it runs; the server writes
 * one mark or one resolution per unit. Everything else is decided one unit at a time.
 */
export default function BulkDecisions({
  review,
  rows,
  candidates,
  canMark,
  canEdit,
  onChanged,
}: {
  review: Review;
  rows: readonly OutlineRow[];
  candidates: CandidateListing | null;
  canMark: boolean;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const markable = canMark ? bulkMarkableRows(rows, review) : [];
  const keepable = canEdit && candidates !== null ? bulkKeepableCandidates(candidates.candidates, candidates.bulkKeepThreshold) : [];
  if (!canMark && !canEdit) {
    return null;
  }
  return (
    <section className="space-y-2" aria-labelledby={headingId}>
      <h4 id={headingId} className="app-subsection-title text-sm">
        {t("reviews.collaboration.bulk.title")}
      </h4>
      {markable.length === 0 && keepable.length === 0 ? <p className="app-text-muted text-sm">{t("reviews.collaboration.bulk.nothing")}</p> : null}
      {markable.length > 0 ? (
        <Checklist
          key={`marks:${markable.map((row) => row.key).join("|")}`}
          options={markable.map((row) => ({ id: row.key, label: row.name }))}
          offer={(count) => t("reviews.collaboration.bulk.markOffer", { count })}
          confirm={(count) => t("reviews.collaboration.bulk.markConfirm", { count })}
          needsReason={false}
          run={async (ids) => {
            await markUnitsOk(review.changeSet.changeSetId, review.head, ids);
            onChanged();
          }}
        />
      ) : null}
      {keepable.length > 0 && candidates !== null ? (
        <Checklist
          key={`keep:${keepable.map((view) => view.candidate.candidateId).join("|")}`}
          options={keepable.map((view) => ({
            id: view.candidate.candidateId,
            label: t("reviews.collaboration.bulk.keepRow", {
              term: view.termSummary?.label ?? iriLocalName(view.candidate.term),
              target: view.targetSummary?.label ?? iriLocalName(view.candidate.target),
              score: view.candidate.scores.total.toFixed(2),
            }),
          }))}
          offer={(count) => t("reviews.collaboration.bulk.keepOffer", { count, threshold: candidates.bulkKeepThreshold })}
          confirm={(count) => t("reviews.collaboration.bulk.keepConfirm", { count })}
          needsReason={true}
          run={async (ids, reason) => {
            await keepNewTerms(review.changeSet.changeSetId, review.head, ids, reason);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

/** One bulk action: offered with its count, then the exact list to narrow and confirm. */
function Checklist({
  options,
  offer,
  confirm,
  needsReason,
  run,
}: {
  options: readonly { readonly id: string; readonly label: string }[];
  offer: (count: number) => string;
  confirm: (count: number) => string;
  needsReason: boolean;
  run: (ids: readonly string[], reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set(options.map((option) => option.id)));
  const [reason, setReason] = useState("");
  const running = useSubmission();
  const ids = options.filter((option) => chosen.has(option.id)).map((option) => option.id);
  if (!open) {
    return (
      <AppButton
        size="sm"
        variant="secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        {offer(options.length)}
      </AppButton>
    );
  }
  return (
    <form
      className="app-card space-y-2 p-2 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        running.submit(async () => {
          await run(ids, reason.trim());
          setOpen(false);
        });
      }}
    >
      <ul className="max-h-60 space-y-1 overflow-y-auto">
        {options.map((option) => (
          <li key={option.id}>
            <OptionLabel>
              <input
                type="checkbox"
                checked={chosen.has(option.id)}
                onChange={(event) => {
                  const next = new Set(chosen);
                  if (event.target.checked) {
                    next.add(option.id);
                  } else {
                    next.delete(option.id);
                  }
                  setChosen(next);
                }}
              />
              <span>{option.label}</span>
            </OptionLabel>
          </li>
        ))}
      </ul>
      {needsReason ? (
        <input
          className={FORM_COMPACT_CONTROL_CLASS}
          aria-label={t("reviews.collaboration.bulk.reason")}
          placeholder={t("reviews.collaboration.bulk.reason")}
          value={reason}
          maxLength={2000}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      ) : null}
      <div className="flex gap-2">
        <AppButton size="sm" type="submit" disabled={ids.length === 0 || (needsReason && reason.trim() === "") || running.state.status === "submitting"}>
          {confirm(ids.length)}
        </AppButton>
        <AppButton
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(false);
          }}
        >
          {t("common.cancel")}
        </AppButton>
      </div>
      {running.state.status === "failed" ? <ErrorNotice error={running.state.error} compact={true} /> : null}
    </form>
  );
}
